import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { animate, state, style, transition, trigger, AnimationEvent } from '@angular/animations';
import { combineLatest, forkJoin, Observable, Subscription, first } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { DomHandler } from 'primeng/dom';
import { ConfigService } from 'app/app-config.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select } from '@ngxs/store';
import { OrganizationState } from '@state/organization.state';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { UserState } from '@state/user.state';
import { ClientAppConfig } from 'app/@types/shared/app-config';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[app-menuitem]',
    template: `
        <ng-container *ngIf="!item.isFreeTrialItem && !item.isMenuItem">
            <div *ngIf="root && item.visible !== false" class="layout-menuitem-root-text">
                {{ item.label }}
            </div>
            <a
                *ngIf="(!item.routerLink || item.items) && item.visible !== false"
                [attr.href]="item.url"
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                [ngClass]="item.disabled === true ? item.class + ' p-disabled ' : item.class"
                [routerLink]="item.routerLink ?? null"
                [attr.target]="item.target"
                tabindex="0"
                pRipple
                [pTooltip]="item.label"
                [tooltipDisabled]="!(isSlim && root && !active)">
                <i *ngIf="item.icon" class="layout-menuitem-icon material-symbols-outlined">{{ item.icon }}</i>
                <span class="layout-menuitem-text">{{ item.label }}</span>
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
            </a>
            <a
                *ngIf="item.routerLink && !item.items && item.visible !== false"
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                [ngClass]="item.disabled === true ? item.class + ' p-disabled ' : item.class"
                [routerLink]="item.routerLink"
                routerLinkActive="active-route"
                [routerLinkActiveOptions]="
                    item.routerLinkActiveOptions || {
                        paths: 'exact',
                        queryParams: 'ignored',
                        matrixParams: 'ignored',
                        fragment: 'ignored'
                    }
                "
                [fragment]="item.fragment"
                [queryParamsHandling]="item.queryParamsHandling"
                [preserveFragment]="item.preserveFragment"
                [skipLocationChange]="item.skipLocationChange"
                [replaceUrl]="item.replaceUrl"
                [state]="item.state"
                [queryParams]="item.queryParams"
                [attr.target]="item.target"
                tabindex="0"
                pRipple
                [pTooltip]="item.label"
                [tooltipDisabled]="!(isSlim && root)">
                <span *ngIf="item.icon" class="layout-menuitem-icon material-symbols-outlined">{{ item.icon }}</span>
                <span class="layout-menuitem-text">{{ item.label }}</span>
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
            </a>

            <ul
                #submenu
                *ngIf="item.items && item.visible !== false"
                [@children]="submenuAnimation"
                (@children.done)="onSubmenuAnimated($event)">
                <ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
                    <li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child.badgeClass"></li>
                </ng-template>
            </ul>
        </ng-container>
        <ng-container *ngIf="item.isFreeTrialItem && item.visible !== false">
            <div class="payment-reminder p-shadow-3" [ngStyle]="{ 'background-color': bgColor }">
                <p [innerHTML]="translateService.get('menu.pricingPlan.text', { days: daysValidity }) | async"></p>
                <button
                    pButton
                    type="button"
                    [label]="translateService.get('menu.pricingPlan.button') | async"
                    class="p-button"
                    (click)="onClickContactUs()"></button>
            </div>
        </ng-container>
        <ng-container *ngIf="item.isMenuItem === true">
            <app-menu-language-selector></app-menu-language-selector>
        </ng-container>
    `,
    styles: [
        `
            .payment-reminder {
                margin-bottom: 15px;
                color: white !important;
                padding: 20px;
                text-align: center;
                width: 100%;
                border-radius: 10px;
                font-family: Arial, sans-serif;
                margin-bottom: 70px;
            }

            .payment-reminder .p-button {
                background-color: white;
                color: var(--primary-color);
                border: none;
                padding: 10px 20px;
                margin-top: 10px;
                border-radius: 5px;
                cursor: pointer;
                font-weight: bold;
            }

            .payment-reminder .p-button:hover {
                background-color: #f2f2f2;
                color: var(--primary-color);
            }
        `,
    ],
    animations: [
        trigger('children', [
            state(
                'collapsed',
                style({
                    height: '0',
                }),
            ),
            state(
                'expanded',
                style({
                    height: '*',
                }),
            ),
            state(
                'hidden',
                style({
                    display: 'none',
                }),
            ),
            state(
                'visible',
                style({
                    display: 'block',
                }),
            ),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
        ]),
    ],
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
    @Input() item: any;

    @Input() index!: number;

    @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

    @Input() parentKey!: string;

    @ViewChild('submenu') submenu!: ElementRef;
    active = false;

    menuSourceSubscription: Subscription;

    menuResetSubscription: Subscription;

    key: string = '';
    @Select(UserState.getCurrentUser) currentUser$: Observable<any>;
    @Select(OrganizationState.getOrganizationPricingPlan)
    organizationPricingPlan$: Observable<OrganizationPricingPlanDto>;

    client: ClientAppConfig;
    bgColor: string;
    dCarbonizeMail: string;
    orgId: string = '';
    daysValidity: number = 0;
    currentUser: { id: string; lastName: string; firstName: string } | null;

    constructor(
        private configService: ConfigService,
        public layoutService: LayoutService,
        private cd: ChangeDetectorRef,
        public router: Router,
        private appSidebar: AppSidebarComponent,
        private menuService: MenuService,
        private translateService: CustomTranslateService,
    ) {
        this.client = this.configService.getClient;
        this.bgColor = this.client?.mainColor ?? '#1e90ff';
        this.dCarbonizeMail = this.client.support?.mailDcarbonize ?? '';

        this.menuSourceSubscription = this.menuService.menuSource$.subscribe((value) => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
                } else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                }
            });
        });

        this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((params) => {
            if (this.isSlimPlus || this.isSlim || this.isHorizontal) {
                this.active = false;
            } else {
                if (this.item.routerLink) {
                    this.updateActiveStateFromRoute();
                }
            }
        });

        combineLatest([this.organizationPricingPlan$, this.currentUser$]).subscribe(([pricingPlan, currentUser]) => {
            if (pricingPlan) {
                const today = new Date();
                const validUntil = new Date(pricingPlan.validUntil);
                const differenceInMilliseconds = validUntil.getTime() - today.getTime();

                const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
                this.daysValidity = differenceInDays + 1;
                this.orgId = pricingPlan.organizationId;
            }
            if (currentUser) {
                this.currentUser = currentUser;
            }
        });
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        if (!(this.isSlimPlus || this.isSlim || this.isHorizontal) && this.item.routerLink) {
            this.updateActiveStateFromRoute();
        }
    }

    ngAfterViewChecked() {
        if (
            this.root &&
            this.active &&
            this.layoutService.isDesktop() &&
            (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())
        ) {
            this.calculatePosition(this.submenu?.nativeElement, this.submenu?.nativeElement.parentElement);
        }
    }
    public onClickContactUs() {
        const name =
            this.currentUser?.firstName && this.currentUser?.lastName
                ? `${this.currentUser?.firstName} ${this.currentUser.lastName}`
                : 'Undefined';

        const organisationId = this.orgId;

        forkJoin({
            subjectTranslation: this.translateService.get('menu.pricingPlan.mail.subject', { name }),
            bodyTranslation: this.translateService.get('menu.pricingPlan.mail.body', {
                name,
                organisationId,
                clientName: name,
            }),
        }).subscribe(({ subjectTranslation, bodyTranslation }) => {
            const subject = encodeURIComponent(subjectTranslation as string);
            const body = encodeURIComponent(bodyTranslation as string);
            const mail = `mailto:${this.dCarbonizeMail}?subject=${subject}&body=${body}`;
            window.open(mail, 'emailWindow');
        });
    }

    updateActiveStateFromRoute() {
        let activeRoute = this.router.isActive(this.item.routerLink[0], {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
        });

        if (activeRoute) {
            this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }
    onSubmenuAnimated(event: AnimationEvent) {
        if (
            event.toState === 'visible' &&
            this.layoutService.isDesktop() &&
            (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())
        ) {
            const el = <HTMLUListElement>event.element;
            const elParent = <HTMLUListElement>el.parentElement;
            this.calculatePosition(el, elParent);
        }
    }

    calculatePosition(overlay: HTMLElement, target: HTMLElement) {
        if (overlay) {
            const { left, top } = target.getBoundingClientRect();
            const [vWidth, vHeight] = [window.innerWidth, window.innerHeight];
            const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
            const scrollbarWidth = DomHandler.calculateScrollbarWidth();
            // reset
            overlay.style.top = '';
            overlay.style.left = '';

            if (this.layoutService.isHorizontal()) {
                const width = left + oWidth + scrollbarWidth;
                overlay.style.left = vWidth < width ? `${left - (width - vWidth)}px` : `${left}px`;
            } else if (this.layoutService.isSlim() || this.layoutService.isSlimPlus()) {
                const height = top + oHeight;
                overlay.style.top = vHeight < height ? `${top - (height - vHeight)}px` : `${top}px`;
            }
        }
    }

    itemClick(event: Event) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // navigate with hover
        if ((this.root && this.isSlim) || this.isHorizontal || this.isSlimPlus) {
            this.layoutService.state.menuHoverActive = !this.layoutService.state.menuHoverActive;
        }

        // execute command
        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // toggle active state
        if (this.item.items) {
            this.active = !this.active;

            if (this.root && this.active && (this.isSlim || this.isHorizontal || this.isSlimPlus)) {
                this.layoutService.onOverlaySubmenuOpen();
            }
        } else {
            if (this.layoutService.isMobile()) {
                this.layoutService.state.staticMenuMobileActive = false;
            }

            if (this.isSlim || this.isHorizontal || this.isSlimPlus) {
                this.menuService.reset();
                this.layoutService.state.menuHoverActive = false;
            }
        }

        this.menuService.onMenuStateChange({ key: this.key });
    }

    onMouseEnter() {
        // activate item on hover
        if (this.root && (this.isSlim || this.isHorizontal || this.isSlimPlus) && this.layoutService.isDesktop()) {
            if (this.layoutService.state.menuHoverActive) {
                this.active = true;
                this.menuService.onMenuStateChange({ key: this.key });
            }
        }
    }

    get submenuAnimation() {
        if (
            this.layoutService.isDesktop() &&
            (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())
        )
            return this.active ? 'visible' : 'hidden';
        else return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
    }

    get isHorizontal() {
        return this.layoutService.isHorizontal();
    }

    get isSlim() {
        return this.layoutService.isSlim();
    }

    get isSlimPlus() {
        return this.layoutService.isSlimPlus();
    }

    @HostBinding('class.active-menuitem')
    get activeClass() {
        return !this.item.items && this.active && !this.root;
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }
}
